import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { match } from 'react-router-dom';

// Components
import App from '../../../components/app';
import ErrorMessage from '../../../components/error-message';
import EnhancedForm from '../../../components/form';
import { FormDatas, FormPickerDatas } from '../../../local-types';
// Modules
import { AccountType, RiderById, TParams } from '../../../redux/reducers/api-types';
import { ErrorPayload, selectors as errorSelectors } from '../../../redux/reducers/errors';
import {
    actions as riderActions,
    selectors as riderSelectors,
    types as riderTypes,
} from '../../../redux/reducers/riders';
import { selectors as pendingSelectors } from '../../../redux/reducers/pendingOperations';
import { AccountRight, RightRank } from '../../../utils/userUtils';
import { paths } from '../../paths';

interface RiderAddPromoProps extends WithTranslation {
    match: match<TParams>;
    riders: RiderById;
    saveRiderPromoError: ErrorPayload;
    saveRiderPromo: (id: string, payload: any) => any;
    isSavingPromo: boolean;
}

interface RiderAddPromoState {
    formData: any;
}

class RiderAddPromoPage extends React.Component<RiderAddPromoProps, RiderAddPromoState> {
    state = {
        formData: {
            use_limit: 1,
            deducted_from_partner_bill: false,
            type: AccountType.PERSO,
            strategy: 'fixed',
        },
    };

    generateTypeOptions = (): FormPickerDatas[] => {
        const typeOptions: FormPickerDatas[] = [
            {
                id: AccountType.PERSO,
                name: AccountType.PERSO,
            },
            {
                id: AccountType.PRO,
                name: AccountType.PRO,
            },
        ];
        return typeOptions;
    };

    generateStrategyOptions = (): FormPickerDatas[] => {
        const { t } = this.props;
        const strategyOptions: FormPickerDatas[] = [
            {
                id: 'fixed',
                name: t('promotions.fixed'),
            },
            {
                id: 'percent',
                name: t('promotions.percent'),
            },
        ];
        return strategyOptions;
    };

    generateInputs = (): FormDatas[] => {
        const { t } = this.props;

        const inputs: FormDatas[] = [
            {
                title: t('common:form.general_infos'),
                datas: [
                    {
                        id: 'deducted_from_partner_bill',
                        label: t('promotions.deducted_from_partner_bill'),
                        type: 'boolean',
                        required: true,
                    },
                    {
                        id: 'use_limit',
                        label: t('promotions.use_limit'),
                        hint: t('promotions.use_limit_hint'),
                        type: 'number',
                        required: true,
                    },
                    {
                        id: 'type',
                        label: t('promotions.type'),
                        options: this.generateTypeOptions(),
                        noEmpty: true,
                        required: true,
                    },
                    {
                        id: 'company',
                        label: t('promotions.company'),
                        placeholder: '123',
                        type: 'number',
                        linked: { id: 'type', value: AccountType.PRO },
                        required: true,
                    },
                    {
                        id: 'strategy',
                        label: t('promotions.strategy'),
                        options: this.generateStrategyOptions(),
                        noEmpty: true,
                        required: true,
                    },
                    {
                        id: 'amount',
                        label: t('promotions.strategy_fixed_amount'),
                        placeholder: '5',
                        type: 'number',
                        required: true,
                    },
                    { id: 'name', label: t('promotions.name'), required: true },
                    { id: 'code', label: t('promotions.code'), placeholder: 'PROMO2020' },
                    { id: 'description', label: t('promotions.description') },
                    {
                        id: 'until_date',
                        label: t('promotions.until_date'),
                        hint: t('promotions.until_date_hint'),
                        type: 'datetime-local',
                    },
                    //sharing_commission_partner_id
                ],
            },
        ];
        return inputs;
    };

    handleSubmit = (formData: any, event): void => {
        const { saveRiderPromo, match } = this.props;
        const id = match.params.id;
        this.setState({
            formData: formData,
        });
        saveRiderPromo(id, formData);
    };

    render() {
        const { match, saveRiderPromoError, isSavingPromo } = this.props;
        const id = match.params.id;
        const formPayload = this.state.formData;

        return (
            <App needRights={{ name: AccountRight.PROMO_DETAILS, mode: RightRank.W }} noFav>
                <ErrorMessage payload={saveRiderPromoError} />
                {formPayload && (
                    <>
                        <EnhancedForm
                            payload={formPayload}
                            inputs={this.generateInputs()}
                            backRoute={`${paths.riders}/${id}`}
                            onSubmit={this.handleSubmit}
                            loader={isSavingPromo}
                        />
                    </>
                )}
            </App>
        );
    }
}

const RiderAddPromoPageWithTranslation = withTranslation(['rider', 'common'])(RiderAddPromoPage);

const mapStateToProps = (state) => ({
    riders: riderSelectors.riders(state),
    saveRiderPromoError: errorSelectors.saveRiderPromoError(state),
    isSavingPromo: pendingSelectors.isPending(state, riderTypes.SAVE_RIDER_PROMO),
});

const mapDispatchToProps = {
    saveRiderPromo: riderActions.saveRiderPromo,
};

export default connect(mapStateToProps, mapDispatchToProps)(RiderAddPromoPageWithTranslation);
